import { Column, Row } from 'further-ui/components/Layout';
import { FC } from 'react';
import FormField from 'further-ui/components/FormField';
import { Button, IconButton, TextField } from '@mui/material';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Control } from 'react-hook-form';
import { TrancheFormInput } from '../../useTrancheForm';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { Delete } from '@mui/icons-material';
import AvatarWithName from 'components/new/AvatarWithName';

const schema = z.object({
  email: z.string().email('Please enter a valid email address.'),
});

type FormInput = z.infer<typeof schema>;

type EmailRow = { id: string };

type Props = {
  control: Control<TrancheFormInput>;
};

const DailySummaryEmail: FC<Props> = ({ control }) => {
  const { field } = useController({ control, name: 'additionalInfo' });

  const emails: Array<EmailRow> = field.value
    ? field.value
        .split(';')
        .map((e) => e.trim())
        .filter(Boolean)
        .map((e) => ({ id: e }))
    : [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormInput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const onAddEmail = handleSubmit(({ email }) => {
    reset();
    if (emails.some((e) => e.id === email)) return;
    field.onChange([...emails.map((e) => e.id), email].join(';'));
  });

  const onRemoveEmail = (row: EmailRow) => {
    const updated = emails
      .map((e) => e.id)
      .filter((e) => e !== row.id)
      .join(';');
    field.onChange(updated);
  };

  const columns: Array<TableColumn<EmailRow>> = [
    {
      label: 'Email address',
      key: 'id',
      render: (row) => (
        <Row fullWidth justify="space-between" centered>
          <AvatarWithName avatarSize="lg" name={row.id} />
          <IconButton size="small" onClick={() => onRemoveEmail(row)}>
            <Delete fontSize="small" />
          </IconButton>
        </Row>
      ),
    },
  ];

  return (
    <Column spacing="lg" fullWidth>
      <FormField
        label="Daily summary email"
        extraInformation="Add people to receive daily summary emails for the tranche."
        size="md"
      >
        <Column spacing="sm" fullWidth>
          <form onSubmit={onAddEmail}>
            <Row spacing="sm" centered>
              <TextField
                {...register('email')}
                fullWidth
                size="small"
                placeholder="Enter email address"
              />
              <Button
                type="submit"
                variant="contained"
                size="medium"
                disabled={!isValid}
              >
                Add
              </Button>
            </Row>
          </form>
        </Column>
      </FormField>
      <FormField size="xl">
        {emails.length > 0 && (
          <Table hideHeader columns={columns} rows={emails} />
        )}
      </FormField>
    </Column>
  );
};

export default DailySummaryEmail;
